import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { getFoamWinderLatest, type FoamWinderStatusRow } from "../../api/foam";

/**
 * Foam winder run status and control mode.
 *
 * Shared by the Foam page and the Overview foam card so the two can't disagree.
 *
 * The winder worker writes every poll rather than skipping unchanged readings,
 * so the row's age is a direct staleness signal: past `WINDER_STALE_AFTER_S` the last
 * reading stops being evidence of anything and this says "no data" instead of
 * showing a stale state as if it were current.
 */

const REFRESH_MS = 30_000;
/**
 * Four missed 30s polls. Long enough to ride out a blip, short enough that a
 * dead worker does not sit here looking healthy. Exported because the overview
 * diagram judges the same row, and the two must not disagree about when it
 * stopped counting.
 */
export const WINDER_STALE_AFTER_S = 120;

/** "12s ago" / "3m ago" / "5h ago" / "2d ago". */
function humanizeAgo(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

type Props = {
  /** Heading above the chips. Omit for a bare row, e.g. inside another card. */
  title?: string;
};

export const FoamWinderStatus: React.FC<Props> = ({ title = "Winder" }) => {
  const [row, setRow] = useState<FoamWinderStatusRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setRow(await getFoamWinderLatest(signal));
      setFailed(false);
    } catch (e: any) {
      if (e?.name !== "CanceledError") setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    const id = setInterval(() => load(), REFRESH_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const stale = !!row && row.age_seconds > WINDER_STALE_AFTER_S;
  const known = !!row && !stale;

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
          {title}
        </Typography>
      )}

      {loading ? (
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={92} height={24} />
          <Skeleton variant="rounded" width={78} height={24} />
        </Stack>
      ) : failed || !row ? (
        <Typography variant="caption" color="text.secondary">
          {failed ? "Could not load winder status." : "No winder readings yet."}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* Status and mode are independent: a winder can sit stopped in auto. */}
          <Chip
            size="small"
            label={!known ? "Status —" : row.status ? "Running" : "Stopped"}
            color={!known ? "default" : row.status ? "success" : "default"}
            variant={known && row.status ? "filled" : "outlined"}
          />
          <Chip
            size="small"
            label={!known ? "Mode —" : row.auto_mode ? "Auto" : "Manual"}
            color={!known ? "default" : row.auto_mode ? "info" : "warning"}
            variant="outlined"
          />
          <Tooltip
            title={
              stale
                ? `Last reading ${humanizeAgo(row.age_seconds)} — older than ` +
                  `${WINDER_STALE_AFTER_S}s, so the state above is not current`
                : `Last reading ${humanizeAgo(row.age_seconds)} from ${row.plc}`
            }
          >
            <Typography
              variant="caption"
              color={stale ? "error" : "text.secondary"}
            >
              {stale ? "no data · " : ""}
              {humanizeAgo(row.age_seconds)}
            </Typography>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
};

export default FoamWinderStatus;
